import { currentTimeLogSetting } from '../../utils/currentTimeLogSetting';
import DANISH_HOLIDAYS from '../../constants/time/danishHolidays';
import { convertToUTC } from '../../utils/formatDates';
import { Timestamp } from 'firebase/firestore';

export const calculateUserMetrics = (
  logs,
  userInfoData,
  companyData,
  dateRange,
  employeeEndDate = null
) => {
  const metrics = {
    normTime: 0,
    totalWorkTime: 0,
    total: 0,
    overtime: {
      count: 0,
      hours: 0,
      hasNote: false,
    },
    sickLeave: {
      count: 0,
      hours: 0,
      hasNote: false,
    },
    timeOff: {
      count: 0,
      hours: 0,
      hasNote: false,
      timeOffType: '',
      byType: {}, // Track hours by timeOffType UUID
    },
    holidays: {
      count: 0,
      hours: 0,
      hasNote: false,
    },
    work: {
      count: 0,
      hours: 0,
      hasNote: false,
    },
    avgHoursPerWeek: 0,
  };

  const datesInRange = [];
  let currentDate = convertToUTC(new Date(dateRange.startDate));

  while (currentDate <= convertToUTC(new Date(dateRange.endDate))) {
    datesInRange.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const logsByDate = logs.reduce((acc, log) => {
    acc[convertToUTC(new Date(log.date)).toISOString().split('T')[0]] = log;
    return acc;
  }, {});

  let totalWorkingDays = 0;

  datesInRange.forEach((dateString) => {
    const date = new Date(dateString);

    // If employee has an endDate, don't calculate norm time beyond that date
    if (employeeEndDate) {
      // Use the same UTC conversion logic as used for date range generation
      const endDateUTC = convertToUTC(employeeEndDate.toDate());
      const endDateString = endDateUTC.toISOString().split('T')[0];

      // Skip dates after the employee's end date (but include the end date itself)
      if (dateString > endDateString) {
        return; // Skip this date as it's after the employee's end date
      }
    }

    const log = logsByDate[dateString];
    const timeLogSetting = currentTimeLogSetting(
      companyData,
      userInfoData,
      date
    );

    if (!timeLogSetting) {
      return;
    }

    // Check if the day is a Danish holiday
    const isHoliday = DANISH_HOLIDAYS.some(
      (holiday) => new Date(holiday.value).getTime() === date.getTime()
    );

    // Date is in the future
    const isFutureDate = date > new Date();

    let normTime = 0;
    if (
      !isFutureDate &&
      timeLogSetting &&
      !timeLogSetting.notWorking &&
      !isHoliday
    ) {
      totalWorkingDays++;

      // Calculate norm hours
      normTime =
        (timeLogSetting.end.toMillis() - timeLogSetting.from.toMillis()) /
        (1000 * 60 * 60);

      // Handle break duration
      if (timeLogSetting.breakDuration > 0) {
        let breakDuration = timeLogSetting.breakDuration / 60;
        if (timeLogSetting.paidBreak) {
          breakDuration = 0;
        }
        normTime -= breakDuration;
      }

      // Add norm hours to total norm hours
      metrics.normTime += normTime;
    }

    let total = 0;
    let totalWorkTime = 0;
    let overtime = { count: 0, hours: 0, hasNote: false };
    let timeOff = {
      count: 0,
      hours: 0,
      hasNote: false,
      timeOffType: '',
      byType: {},
    };
    let holiday = { count: 0, hours: 0, hasNote: false };
    let sick = { count: 0, hours: 0, hasNote: false };
    let work = { count: 0, hours: 0, hasNote: false };

    const isHourlyPaid = timeLogSetting.isPaidHourly;

    // Adjust for log entries if they exist
    if (log) {
      log.entries.forEach((entry) => {
        const hasNote = entry.note && entry.note.trim() !== '';
        switch (entry.type) {
          case 'overtime':
            overtime.hours += entry.hours;
            overtime.hasNote = overtime.hasNote || hasNote;
            overtime.count += 1;
            break;
          case 'timeOff':
            timeOff.hours += entry.hours;
            timeOff.hasNote = timeOff.hasNote || hasNote;
            timeOff.timeOffType = entry.timeOffType;
            timeOff.count += 1;
            // Track by timeOffType UUID
            if (entry.timeOffType) {
              if (!timeOff.byType[entry.timeOffType]) {
                timeOff.byType[entry.timeOffType] = 0;
              }
              timeOff.byType[entry.timeOffType] += entry.hours;
            }
            if (!isHourlyPaid) work.hours -= entry.hours;
            break;
          case 'holiday':
            holiday.hours += entry.hours;
            holiday.hasNote = holiday.hasNote || hasNote;
            holiday.count += 1;
            if (!isHourlyPaid) work.hours -= entry.hours;
            break;
          case 'sickLeave':
            sick.hours += entry.hours;
            sick.hasNote = sick.hasNote || hasNote;
            sick.count += 1;
            if (!isHourlyPaid) work.hours -= entry.hours;
            break;
          case 'work':
            work.hours += entry.hours;
            work.hasNote = work.hasNote || hasNote;
            work.count += 1;
            break;
          default:
            break;
        }
      });
    }

    work.hours += normTime;

    // Calculate total work time
    totalWorkTime = work.hours + overtime.hours;

    // Calculate total time -- this is work hours, overtime, holiday and sick leave, but not time off
    total = totalWorkTime + holiday.hours + sick.hours;

    // Update metrics
    metrics.totalWorkTime += totalWorkTime;
    metrics.overtime.hours += overtime.hours;
    metrics.overtime.hasNote = metrics.overtime.hasNote || overtime.hasNote;
    metrics.overtime.count += overtime.count;
    metrics.sickLeave.hours += sick.hours;
    metrics.sickLeave.hasNote = metrics.sickLeave.hasNote || sick.hasNote;
    metrics.sickLeave.count += sick.count;
    metrics.timeOff.hours += timeOff.hours;
    metrics.timeOff.hasNote = metrics.timeOff.hasNote || timeOff.hasNote;
    metrics.timeOff.timeOffType =
      timeOff.timeOffType || metrics.timeOff.timeOffType;
    metrics.timeOff.count += timeOff.count;
    // Merge byType data
    Object.keys(timeOff.byType).forEach((typeUUID) => {
      if (!metrics.timeOff.byType[typeUUID]) {
        metrics.timeOff.byType[typeUUID] = 0;
      }
      metrics.timeOff.byType[typeUUID] += timeOff.byType[typeUUID];
    });
    metrics.holidays.hours += holiday.hours;
    metrics.holidays.hasNote = metrics.holidays.hasNote || holiday.hasNote;
    metrics.holidays.count += holiday.count;
    metrics.work.hours += work.hours;
    metrics.work.hasNote = metrics.work.hasNote || (log && log.hasNote);
    metrics.work.count += work.count;
    metrics.total += total;
  });

  const weeks = totalWorkingDays / 5; // assuming 5 working days per week
  metrics.avgHoursPerWeek = weeks > 0 ? metrics.totalWorkTime / weeks : 0;

  return metrics;
};

export const calculateAllUsersMetrics = (
  logsByUser,
  employees,
  companyData,
  dateRange,
  orderByDate = false
) => {
  if (orderByDate) {
    const metricsByDate = {};
    const datesInRange = [];
    let currentDate = convertToUTC(new Date(dateRange.startDate));

    while (currentDate <= convertToUTC(new Date(dateRange.endDate))) {
      datesInRange.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    datesInRange.forEach((dateString) => {
      metricsByDate[dateString] = {};
      employees.forEach((employee) => {
        const userId = employee.id;
        const userLogs = logsByUser[userId] || [];
        const userLogsForDate = userLogs.filter(
          (log) =>
            convertToUTC(new Date(log.date)).toISOString().split('T')[0] ===
            dateString
        );
        const metrics = calculateUserMetrics(
          userLogsForDate,
          employee.data,
          companyData,
          { startDate: dateString, endDate: dateString },
          employee.data.endDate // Pass the employee's endDate
        );
        metricsByDate[dateString][userId] = metrics;
      });
    });

    return metricsByDate;
  } else {
    const allUsersMetrics = {};

    employees.forEach((employee) => {
      const userId = employee.id;
      const userLogs = logsByUser[userId] || [];
      allUsersMetrics[userId] = calculateUserMetrics(
        userLogs,
        employee.data,
        companyData,
        dateRange,
        employee.data.endDate // Pass the employee's endDate
      );
    });

    return allUsersMetrics;
  }
};

export const convertTimeOffTypeUUIDToName = (timeOffTypeUUID, timeOffTypes) => {
  const timeOffType = timeOffTypes.find(
    (type) => type.value === timeOffTypeUUID
  );
  return timeOffType ? timeOffType.label : '';
};

export const getDefaultWeeklySchedule = () => {
  const setWorkHours = (hours, minutes = 0) => {
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    return Timestamp.fromDate(date);
  };

  return [
    {
      day: 'Monday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: false,
    },
    {
      day: 'Tuesday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: false,
    },
    {
      day: 'Wednesday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: false,
    },
    {
      day: 'Thursday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: false,
    },
    {
      day: 'Friday',
      end: setWorkHours(15, 30),
      from: setWorkHours(8, 0),
      notWorking: false,
    },
    {
      day: 'Saturday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: true,
    },
    {
      day: 'Sunday',
      end: setWorkHours(16, 0),
      from: setWorkHours(8, 0),
      notWorking: true,
    },
  ];
};
